<template>
  <ContentWrap>
    <el-form :inline="true" :model="queryParams" class="-mb-15px">
      <el-form-item label="活动ID">
        <el-input v-model="queryParams.activityId" clearable class="!w-120px" @keyup.enter="handleQuery" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" clearable placeholder="全部" class="!w-140px">
          <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column label="编号" prop="id" width="80" />
      <el-table-column label="批次号" prop="batchNo" min-width="160" />
      <el-table-column label="活动" min-width="160">
        <template #default="{ row }">
          <div>{{ row.activityName || '-' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="自提点" min-width="160">
        <template #default="{ row }">
          <div>{{ row.pickupPointName || '-' }}</div>
          <div v-if="row.pickupPointAddress" class="text-gray-400 text-xs">{{ row.pickupPointAddress }}</div>
        </template>
      </el-table-column>
      <el-table-column label="订单数" width="80">
        <template #default="{ row }">{{ row.orderCount ?? 0 }}</template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template #default="{ row }">{{ statusLabel(row.status) }}</template>
      </el-table-column>
      <el-table-column label="司机" min-width="140">
        <template #default="{ row }">
          <span v-if="row.driverName">{{ row.driverName }} {{ row.driverPhone }}</span>
          <span v-else class="text-gray-400">未派</span>
        </template>
      </el-table-column>
      <el-table-column label="批次运费" prop="batchFee" width="100" />
      <el-table-column label="异常" prop="abnormalReason" min-width="120" show-overflow-tooltip />
      <el-table-column label="操作" width="320" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">详情</el-button>
          <el-button
            v-if="row.status === 'pending' || row.status === 'assigned' || row.status === 'abnormal'"
            link
            type="primary"
            @click="openAssign(row)"
          >
            {{ row.status === 'abnormal' ? '改派' : '派批' }}
          </el-button>
          <el-button
            v-if="row.status === 'pending' || row.status === 'assigned'"
            link
            type="success"
            @click="openProof(row, 'picked')"
          >
            代点取货
          </el-button>
          <el-button
            v-if="row.status === 'picked'"
            link
            type="success"
            @click="openProof(row, 'delivered')"
          >
            代点送达
          </el-button>
          <el-button
            v-if="row.status !== 'delivered' && row.status !== 'abnormal'"
            link
            type="danger"
            @click="openAbnormal(row)"
          >
            异常
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="detailVisible" title="批次详情" width="860px">
    <div v-loading="detailLoading">
      <el-descriptions v-if="detail" :column="2" border class="mb-16px">
        <el-descriptions-item label="批次号">{{ detail.batchNo }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ statusLabel(detail.status) }}</el-descriptions-item>
        <el-descriptions-item label="活动">
          {{ detail.activityName || '-' }}（{{ detail.activityId }}）
        </el-descriptions-item>
        <el-descriptions-item label="自提点">
          <div>{{ detail.pickupPointName || '-' }}</div>
          <div class="text-gray-400 text-xs">{{ detail.pickupPointAddress }}</div>
        </el-descriptions-item>
        <el-descriptions-item label="司机">
          <span v-if="detail.driverName">{{ detail.driverName }} {{ detail.driverPhone }}</span>
          <span v-else class="text-gray-400">未派</span>
        </el-descriptions-item>
        <el-descriptions-item label="批次运费 / 单数">
          ¥{{ detail.batchFee ?? 0 }} · {{ detail.orderCount ?? 0 }} 单
        </el-descriptions-item>
        <el-descriptions-item v-if="detail.abnormalReason || detail.abnormalCode" label="异常" :span="2">
          <div>
            <el-tag v-if="detail.abnormalCode" size="small" type="danger" class="mr-8px">
              {{ detail.abnormalCode }}
            </el-tag>
            <span>{{ detail.abnormalReason || '-' }}</span>
          </div>
          <div v-if="detail.abnormalTime" class="text-gray-400 text-xs mt-4px">
            {{ detail.abnormalTime }}
          </div>
        </el-descriptions-item>
        <el-descriptions-item
          v-if="splitImgs(detail.abnormalImgs).length"
          label="异常凭证"
          :span="2"
        >
          <div class="batch-proof">
            <el-image
              v-for="(url, i) in splitImgs(detail.abnormalImgs)"
              :key="'a' + i"
              :src="url"
              :preview-src-list="splitImgs(detail.abnormalImgs)"
              :initial-index="i"
              fit="cover"
              class="batch-proof__pic"
            />
          </div>
        </el-descriptions-item>
        <el-descriptions-item v-if="splitImgs(detail.pickupImgs).length" label="取货凭证" :span="2">
          <div class="batch-proof">
            <el-image
              v-for="(url, i) in splitImgs(detail.pickupImgs)"
              :key="'p' + i"
              :src="url"
              :preview-src-list="splitImgs(detail.pickupImgs)"
              :initial-index="i"
              fit="cover"
              class="batch-proof__pic"
            />
          </div>
        </el-descriptions-item>
        <el-descriptions-item v-if="splitImgs(detail.doneImgs).length" label="送达凭证" :span="2">
          <div class="batch-proof">
            <el-image
              v-for="(url, i) in splitImgs(detail.doneImgs)"
              :key="'d' + i"
              :src="url"
              :preview-src-list="splitImgs(detail.doneImgs)"
              :initial-index="i"
              fit="cover"
              class="batch-proof__pic"
            />
          </div>
        </el-descriptions-item>
      </el-descriptions>

      <div class="mb-8px font-medium">跟团订单（{{ detail?.orders?.length || 0 }}）</div>
      <el-empty v-if="!detail?.orders?.length" description="本批暂无订单" :image-size="64" />
      <div v-for="order in detail?.orders || []" :key="order.orderRefId" class="batch-order">
        <div class="batch-order__head">
          <span class="batch-order__no">{{ order.tradeOrderNo || order.tradeOrderId }}</span>
          <span class="batch-order__meta">提货码 {{ order.pickupCode || '-' }}</span>
          <span class="batch-order__meta">实付 ¥{{ fenToYuan(order.payPrice) }}</span>
          <span class="batch-order__meta">分摊运费 ¥{{ order.deliveryFeeShare ?? 0 }}</span>
          <el-tag v-if="order.leaderSorted" size="small" type="success">已理货</el-tag>
          <el-tag v-else size="small" type="info">未理货</el-tag>
          <el-tag
            v-if="order.pickupCodeStatus === 1"
            size="small"
            type="warning"
            class="ml-4px"
          >
            已核销
          </el-tag>
        </div>
        <el-table :data="order.items || []" size="small" border class="mt-8px">
          <el-table-column label="商品" min-width="220">
            <template #default="{ row }">
              <div class="batch-goods">
                <el-image
                  v-if="row.picUrl"
                  :src="row.picUrl"
                  class="batch-goods__pic"
                  fit="cover"
                />
                <span>{{ row.spuName || '-' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="数量" prop="count" width="70" />
          <el-table-column label="单价" width="90">
            <template #default="{ row }">¥{{ fenToYuan(row.price) }}</template>
          </el-table-column>
          <el-table-column label="行实付" width="90">
            <template #default="{ row }">¥{{ fenToYuan(row.payPrice) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </div>
    <template #footer>
      <el-button type="primary" @click="detailVisible = false">关闭</el-button>
    </template>
  </Dialog>

  <Dialog v-model="assignVisible" :title="assignIsReassign ? '改派司机' : '派批给司机'" width="420px">
    <el-alert
      v-if="assignIsReassign"
      class="mb-12px"
      type="warning"
      :closable="false"
      :title="assignAbnormalHint || '该批次处于异常，改派后将清空异常记录并回到待取货'"
    />
    <el-form label-width="80px">
      <el-form-item label="司机">
        <el-select v-model="assignDriverId" filterable placeholder="选择启用司机" class="!w-full">
          <el-option
            v-for="d in drivers"
            :key="d.id"
            :label="`${d.driverName} ${d.driverPhone}`"
            :value="d.id!"
          />
        </el-select>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="assignVisible = false">取消</el-button>
      <el-button type="primary" :loading="opLoading" @click="submitAssign">确定</el-button>
    </template>
  </Dialog>

  <Dialog v-model="proofVisible" :title="proofMode === 'picked' ? '代点取货' : '代点送达'" width="480px">
    <el-form label-width="90px">
      <el-form-item label="凭证图片" required>
        <UploadImgs v-model="proofUrls" :limit="6" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="proofVisible = false">取消</el-button>
      <el-button type="primary" :loading="opLoading" @click="submitProof">确定</el-button>
    </template>
  </Dialog>

  <Dialog v-model="abnormalVisible" title="登记异常" width="420px">
    <el-form label-width="80px">
      <el-form-item label="原因" required>
        <el-input v-model="abnormalReason" type="textarea" :rows="3" maxlength="200" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="abnormalVisible = false">取消</el-button>
      <el-button type="danger" :loading="opLoading" @click="submitAbnormal">确定</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as Api from '@/api/merchant/groupbuy'
import { UploadImgs } from '@/components/UploadFile'
import { fenToYuan } from '@/utils'

defineOptions({ name: 'MerchantGroupbuyBatch' })

const message = useMessage()
const loading = ref(false)
const opLoading = ref(false)
const list = ref<Api.GroupbuyBatchVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  activityId: undefined as string | undefined,
  status: undefined as string | undefined
})

const statusOptions = [
  { value: 'pending', label: '待配送' },
  { value: 'assigned', label: '已派批' },
  { value: 'picked', label: '已取货' },
  { value: 'delivered', label: '已送达' },
  { value: 'abnormal', label: '异常' }
]
const statusLabel = (s?: string) => statusOptions.find((i) => i.value === s)?.label || s || '-'

const splitImgs = (raw?: string) =>
  String(raw || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

const detailVisible = ref(false)
const detailLoading = ref(false)
const detail = ref<Api.GroupbuyBatchDetailVO | null>(null)

const assignVisible = ref(false)
const assignIsReassign = ref(false)
const assignAbnormalHint = ref('')
const assignDriverId = ref<number>()
const currentBatchId = ref<number>()
const drivers = ref<Api.GroupbuyDriverVO[]>([])

const proofVisible = ref(false)
const proofMode = ref<'picked' | 'delivered'>('picked')
const proofUrls = ref<string[]>([])

const abnormalVisible = ref(false)
const abnormalReason = ref('')

const getList = async () => {
  loading.value = true
  try {
    const params: Recordable = {
      pageNo: queryParams.pageNo,
      pageSize: queryParams.pageSize,
      status: queryParams.status
    }
    if (queryParams.activityId) {
      params.activityId = Number(queryParams.activityId)
    }
    const data = await Api.getBatchPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const openDetail = async (row: Api.GroupbuyBatchVO) => {
  if (!row.id) return
  detailVisible.value = true
  detailLoading.value = true
  detail.value = null
  try {
    detail.value = await Api.getBatchDetail(row.id)
  } finally {
    detailLoading.value = false
  }
}

const openAssign = async (row: Api.GroupbuyBatchVO) => {
  currentBatchId.value = row.id
  assignDriverId.value = row.driverId
  assignIsReassign.value = row.status === 'abnormal'
  assignAbnormalHint.value = row.abnormalReason
    ? `当前异常：${row.abnormalReason}。改派后将清空异常记录并回到待取货。`
    : ''
  assignVisible.value = true
  const data = await Api.getDriverPage({ pageNo: 1, pageSize: 100, status: 'active' })
  drivers.value = data.list || []
}
const submitAssign = async () => {
  if (!currentBatchId.value || !assignDriverId.value) {
    message.warning('请选择司机')
    return
  }
  opLoading.value = true
  try {
    await Api.assignBatchDriver(currentBatchId.value, assignDriverId.value)
    message.success(assignIsReassign.value ? '已改派' : '已派批')
    assignVisible.value = false
    await getList()
  } finally {
    opLoading.value = false
  }
}

const openProof = (row: Api.GroupbuyBatchVO, mode: 'picked' | 'delivered') => {
  currentBatchId.value = row.id
  proofMode.value = mode
  proofUrls.value = []
  proofVisible.value = true
}
const submitProof = async () => {
  if (!currentBatchId.value || !proofUrls.value.length) {
    message.warning('请上传至少 1 张凭证')
    return
  }
  opLoading.value = true
  try {
    if (proofMode.value === 'picked') {
      await Api.markBatchPicked(currentBatchId.value, proofUrls.value)
    } else {
      await Api.markBatchDelivered(currentBatchId.value, proofUrls.value)
    }
    message.success('操作成功')
    proofVisible.value = false
    await getList()
  } finally {
    opLoading.value = false
  }
}

const openAbnormal = (row: Api.GroupbuyBatchVO) => {
  currentBatchId.value = row.id
  abnormalReason.value = ''
  abnormalVisible.value = true
}
const submitAbnormal = async () => {
  if (!currentBatchId.value || !abnormalReason.value.trim()) {
    message.warning('请填写异常原因')
    return
  }
  opLoading.value = true
  try {
    await Api.markBatchAbnormal(currentBatchId.value, abnormalReason.value.trim())
    message.success('已登记异常')
    abnormalVisible.value = false
    await getList()
  } finally {
    opLoading.value = false
  }
}

onMounted(getList)
</script>

<style scoped>
.batch-order {
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  background: var(--el-fill-color-blank);
}
.batch-order__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  font-size: 13px;
}
.batch-order__no {
  font-weight: 600;
}
.batch-order__meta {
  color: var(--el-text-color-secondary);
}
.batch-goods {
  display: flex;
  align-items: center;
  gap: 8px;
}
.batch-goods__pic {
  width: 36px;
  height: 36px;
  border-radius: 4px;
  flex-shrink: 0;
}
.batch-proof {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.batch-proof__pic {
  width: 64px;
  height: 64px;
  border-radius: 4px;
}
</style>
